import RSDK from '@sr/functions/rsdk/src';
import { ContentType, SectionDate, SectionType } from '@sr/types';
import equal from 'fast-deep-equal/es6/react';
import React from 'react';
import { Box, Button, Flex, Select, Text } from '../../ui';
import API from '../../utils/api';
import { useHasActiveBlockError } from 'store/selectors/message';
import useSaveElement from 'v1/hooks/use-save-element';
import { SettingsIcon } from 'v1/ui/icons';

type DatePickerProps = {
  date: SectionDate | null | undefined;
  path: string;
  srId: string;
  disabled: boolean;
};

const DatePicker = ({ date, path, srId, disabled }: DatePickerProps) => {
  const [containerRef] = useSaveElement(srId);
  const hasBlockError = useHasActiveBlockError(srId);
  function handleMonthChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const month =
      e.target.value === 'Month' ? null : parseInt(e.target.value, 10);
    API.resume.setProperty({
      path: `${path}/month`,
      value: month,
    });
  }

  function handleYearChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const year =
      e.target.value === 'Year' ? null : parseInt(e.target.value, 10);
    API.resume.setProperty({
      path: `${path}/year`,
      value: year,
    });
  }

  return (
    <Flex>
      <Select
        value={date ? date.month : ''}
        disabled={disabled}
        onChange={handleMonthChange}
        data-testid="change-month"
        sx={{ width: 125 }}
      >
        <option>Month</option>
        <option value="1">January</option>
        <option value="2">February</option>
        <option value="3">March</option>
        <option value="4">April</option>
        <option value="5">May</option>
        <option value="6">June</option>
        <option value="7">July</option>
        <option value="8">August</option>
        <option value="9">September</option>
        <option value="10">October</option>
        <option value="11">November</option>
        <option value="12">December</option>
      </Select>
      <Select
        value={date ? date.year : ''}
        disabled={disabled}
        onChange={handleYearChange}
        data-testid="change-year"
        id={srId}
        ref={containerRef}
        sx={{ width: 80, ml: [1, 3] }}
        variant={hasBlockError ? 'selectError' : 'select'}
      >
        <option>Year</option>
        <option value="2025">2025</option>
        <option value="2024">2024</option>
        <option value="2023">2023</option>
        <option value="2022">2022</option>
        <option value="2021">2021</option>
        <option value="2020">2020</option>
        <option value="2019">2019</option>
        <option value="2018">2018</option>
        <option value="2017">2017</option>
        <option value="2016">2016</option>
        <option value="2015">2015</option>
        <option value="2014">2014</option>
        <option value="2013">2013</option>
        <option value="2012">2012</option>
        <option value="2011">2011</option>
        <option value="2010">2010</option>
        <option value="2009">2009</option>
        <option value="2008">2008</option>
        <option value="2007">2007</option>
        <option value="2006">2006</option>
        <option value="2005">2005</option>
        <option value="2004">2004</option>
        <option value="2003">2003</option>
        <option value="2002">2002</option>
        <option value="2001">2001</option>
        <option value="2000">2000</option>
        <option value="1999">1999</option>
        <option value="1998">1998</option>
        <option value="1997">1997</option>
        <option value="1996">1996</option>
        <option value="1995">1995</option>
        <option value="1994">1994</option>
        <option value="1993">1993</option>
        <option value="1992">1992</option>
        <option value="1991">1991</option>
        <option value="1990">1990</option>
        <option value="1989">1989</option>
        <option value="1988">1988</option>
        <option value="1987">1987</option>
        <option value="1986">1986</option>
        <option value="1985">1985</option>
        <option value="1984">1984</option>
        <option value="1983">1983</option>
        <option value="1982">1982</option>
        <option value="1981">1981</option>
        <option value="1980">1980</option>
        <option value="1979">1979</option>
        <option value="1978">1978</option>
        <option value="1977">1977</option>
        <option value="1976">1976</option>
        <option value="1975">1975</option>
        <option value="1974">1974</option>
        <option value="1973">1973</option>
        <option value="1972">1972</option>
        <option value="1971">1971</option>
        <option value="1970">1970</option>
      </Select>
    </Flex>
  );
};

DatePicker.defaultProps = {
  disabled: false,
};

type CheckboxProps = {
  isCurrent: boolean;
  path: string;
};

const Checkbox = ({ isCurrent, path }: CheckboxProps) => {
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    API.resume.setProperty({
      path: `${path}/isCurrent`,
      value: e.target.checked,
    });
    // Clear the stop date if isCurrent
    if (e.target.checked) {
      API.resume.setProperty({
        path: `${path}/stopDate/month`,
        value: null,
      });
      API.resume.setProperty({
        path: `${path}/stopDate/year`,
        value: null,
      });
    }
  }
  return (
    <input
      type="checkbox"
      checked={isCurrent}
      data-testid="change-is-current"
      onChange={handleChange}
    />
  );
};

const Settings = ({ path }: { path: string }) => {
  function changeCurrentDateString() {
    const resumeId = path.split('/')[0];
    if (resumeId.length < 10) {
      throw new Error(`Invalid resumeId: ${resumeId}`);
    }
    const value = window.prompt('Enter a value for "January 2020 - {VALUE}"');
    if (typeof value === 'string' && value.length > 0) {
      API.resume.setProperty({
        path: `${resumeId}/currentDateString`,
        value,
      });
    }
  }
  return (
    <Button
      className="currentSettings"
      variant="icon"
      sx={{
        opacity: 0,
        transition: 'opacity 150ms linear',
        display: 'flex',
        ml: [1, 2, 3],
        p: 1,
        bg: 'transparent',
        ':hover': {
          opacity: '1 !important',
        },
      }}
      onClick={changeCurrentDateString}
    >
      <SettingsIcon size={24} />
    </Button>
  );
};

type DateRangeProps = {
  path: string;
  startDate: SectionDate | null | undefined;
  stopDate: SectionDate | null | undefined;
  isCurrent: boolean;
  itemId: string;
  sectionType: SectionType;
  contentType: ContentType;
  currentDateString?: string | null;
};

const DateRange = React.memo<DateRangeProps>(
  ({
    path,
    startDate,
    stopDate,
    isCurrent,
    itemId,
    sectionType,
    contentType,
    currentDateString,
  }: DateRangeProps) => (
    <Box
      bg="white"
      sx={{
        borderBottomWidth: 2,
        borderBottomStyle: 'solid',
        borderBottomColor: 'surfaceGray',
        py: 3,
        ':focus-within button.currentSettings': {
          opacity: 0.5,
        },
        ':hover button.currentSettings': {
          opacity: 0.5,
        },
      }}
    >
      <Flex mb={[3, 0]} sx={{ alignItems: 'center' }}>
        <Text
          sx={{
            ml: 3,
            mr: [0, 2],
            width: 40,
            color: 'black50',
          }}
        >
          Start
        </Text>
        <DatePicker
          date={startDate}
          path={`${path}/startDate`}
          srId={RSDK.getItemPropSrId({
            itemId,
            sectionType,
            contentType,
            prop: 'startDate',
          })}
        />
      </Flex>
      <Flex sx={{ alignItems: 'center' }}>
        <Text
          sx={{
            ml: 3,
            mr: [0, 2],
            width: 40,
            color: 'black50',
          }}
        >
          Stop
        </Text>
        <DatePicker
          date={stopDate}
          path={`${path}/stopDate`}
          disabled={isCurrent}
          srId={RSDK.getItemPropSrId({
            itemId,
            sectionType,
            contentType,
            prop: 'stopDate',
          })}
        />
        <Flex
          ml={3}
          sx={{ alignItems: 'center' }}
          id={RSDK.getItemPropSrId({
            itemId,
            sectionType,
            contentType,
            prop: 'isCurrent',
          })}
        >
          <Box as="label" sx={{ display: 'flex' }}>
            <Checkbox isCurrent={isCurrent} path={path} />
            <Text sx={{ ml: 2, userSelect: 'none' }}>
              {currentDateString || 'Current'}
            </Text>
          </Box>
        </Flex>
        <Settings path={path} />
      </Flex>
    </Box>
  ),
  equal
);

export default DateRange;
